import type { User } from '@open-dataroom/shared';
import { redirect } from '@tanstack/react-router';

import { getAuthSnapshot, publishAuthenticatedUser } from '~/lib/auth';

/** Routes that render without the authenticated app shell (sidebar + header). */
export function isPublicAppRoute(pathname: string): boolean {
  if (pathname === '/login') return true;
  if (pathname.startsWith('/auth/')) return true;
  if (pathname.startsWith('/invites/')) return true;
  return false;
}

const API_BASE = import.meta.env.VITE_API_URL || '';
const SESSION_ENDPOINT = '/api/auth/session';
const FETCH_TTL_MS = 5_000;

let sessionPromise: Promise<User | null> | null = null;
let lastFetchResult: { user: User | null; timestamp: number } | null = null;

async function fetchSessionUser(
  options: { forceRefresh?: boolean } = {}
): Promise<User | null> {
  const snapshot = getAuthSnapshot();
  if (!options.forceRefresh && snapshot.status === 'authenticated') {
    return snapshot.user;
  }
  if (!options.forceRefresh && snapshot.status === 'anonymous') {
    return null;
  }

  if (
    !options.forceRefresh &&
    lastFetchResult &&
    Date.now() - lastFetchResult.timestamp < FETCH_TTL_MS
  ) {
    return lastFetchResult.user;
  }

  if (sessionPromise) {
    return sessionPromise;
  }

  sessionPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE}${SESSION_ENDPOINT}`, {
        method: 'GET',
        credentials: 'include'
      });
      if (!response.ok) {
        // Stale probes must not clear a session set by invite redeem / magic-link verify.
        if (
          options.forceRefresh ||
          getAuthSnapshot().status !== 'authenticated'
        ) {
          publishAuthenticatedUser(null);
        }
        lastFetchResult = { user: null, timestamp: Date.now() };
        if (options.forceRefresh) {
          return null;
        }
        const snapshot = getAuthSnapshot();
        return snapshot.status === 'authenticated' ? snapshot.user : null;
      }
      const data = (await response.json()) as { user: User };
      publishAuthenticatedUser(data.user);
      lastFetchResult = { user: data.user, timestamp: Date.now() };
      return data.user;
    } catch {
      if (
        options.forceRefresh ||
        getAuthSnapshot().status !== 'authenticated'
      ) {
        publishAuthenticatedUser(null);
      }
      lastFetchResult = { user: null, timestamp: Date.now() };
      if (options.forceRefresh) {
        return null;
      }
      const snapshot = getAuthSnapshot();
      return snapshot.status === 'authenticated' ? snapshot.user : null;
    } finally {
      sessionPromise = null;
    }
  })();

  return sessionPromise;
}

export async function requireSession() {
  const user = await fetchSessionUser();
  if (!user) {
    throw redirect({ to: '/login' });
  }
}

/**
 * For routes only owners/admins may access (e.g. create room, audit).
 * Viewers are redirected to `redirectToIfViewer` (default: rooms list).
 */
export async function requireOwnerOrAdmin(
  redirectToIfViewer: string = '/rooms/'
) {
  const user = await fetchSessionUser({ forceRefresh: true });
  if (!user) throw redirect({ to: '/login' });
  if (user.role !== 'owner' && user.role !== 'admin') {
    throw redirect({ to: redirectToIfViewer });
  }
}

export async function redirectAuthenticatedToDashboard() {
  const user = await fetchSessionUser();
  if (user) {
    throw redirect({ to: '/dashboard' });
  }
}
